// Authentication service for Cognito operations
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

class AuthService {
  
  /**
   * Change user password using Cognito
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @param {object} auth - Auth context from react-oidc-context
   * @returns {Promise} Promise that resolves when password is changed
   */
  static async changePassword(currentPassword, newPassword, auth) {
    return new Promise(async (resolve, reject) => {
      try {
        // Check if user is authenticated
        if (!auth?.user?.access_token) {
          reject(new Error('Usuario no autenticado'));
          return;
        }

        const userId = auth.user.profile?.sub;
        if (!userId) {
          reject(new Error('No se pudo obtener el ID del usuario'));
          return;
        }

        // Call the backend API to change password
        const response = await fetch(`${API_BASE_URL}/users/${userId}/change-password`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${auth.user.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            currentPassword,
            newPassword
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Error al cambiar la contraseña');
        }

        const data = await response.json();
        resolve(data.message || 'Contraseña cambiada exitosamente');
        
      } catch (error) {
        if (error.message.includes('fetch')) {
          reject(new Error('Error de conexión. Verifica tu conexión a internet.'));
        } else {
          reject(error);
        }
      }
    });
  }

  /**
   * Redirect to Cognito hosted change password page
   * @param {object} auth - Auth context from react-oidc-context  
   */
  static redirectToChangePassword(auth) {
    const cognitoDomain = "https://us-east-1tohvlurxq.auth.us-east-1.amazoncognito.com";
    const clientId = "2furhq1s6v5mbj77uafsr7j660";
    const redirectUri = encodeURIComponent(`${window.location.origin}/settings/profile`);
    
    // Redirect to Cognito's forgot password page which can be used for password changes
    const changePasswordUrl = `${cognitoDomain}/forgotPassword?client_id=${clientId}&response_type=code&redirect_uri=${redirectUri}`;
    
    window.location.href = changePasswordUrl;
  }

  /**
   * Get current user from Cognito
   * @param {object} auth - Auth context from react-oidc-context
   * @returns {object|null} Current user or null
   */
  static getCurrentUser(auth) {
    return auth?.user || null;
  }

  /**
   * Check if user session is valid
   * @param {object} auth - Auth context from react-oidc-context
   * @returns {boolean} True if session is valid
   */
  static isSessionValid(auth) {
    if (!auth?.user?.access_token) {
      return false;
    }

    // Check if token is expired
    const currentTime = Date.now() / 1000;
    return auth.user.expires_at > currentTime;
  }

  /**
   * Refresh user session
   * @param {object} auth - Auth context from react-oidc-context
   */
  static async refreshSession(auth) {
    try {
      await auth.signinSilent();
    } catch (error) {
      console.error('Error refreshing session:', error);
      throw new Error('No se pudo actualizar la sesión');
    }
  }
}

export default AuthService;
